/* eslint-disable @typescript-eslint/member-ordering */
import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { SortMeta } from 'primeng/api';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { Observable } from 'rxjs';
import { SHARED_APP_CONFIG_TOKEN } from '../../../core/tokens/app-config.token';
import { AbstractDataSource } from '../../datasource';
import { TableSort } from '../../models';
import { TableColumn } from '../../models/column.model';
import { getDefaultTableConfig, TableConfig } from '../../models/table.config';
import { DeepObjectPipe, DynamicPipe } from '../../pipes';

@UntilDestroy()
@Component({
    selector: 'lib-ngx-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    standalone: true,
    imports: [
        TranslateModule,
        DynamicPipe,
        TableModule,
        DeepObjectPipe,
        CommonModule,
        ProgressSpinnerModule,
        PaginatorModule
    ]
})
export class TableComponent<T> implements OnInit, OnChanges {
    private _appConfig = inject(SHARED_APP_CONFIG_TOKEN);
    @Input() expandLinkText!: string;
    @Input() rowDetail!: TemplateRef<unknown>;

    /**
     * Source of data that will be displayed.
     */
    @Input({ required: true }) datasource!: AbstractDataSource<T>;

    @Input() config!: TableConfig;

    /**
     * Columns to be displayed.
     */
    @Input() columns!: TableColumn<T>[];

    /**
     * Available size options
     */
    @Input() pageSizeOptions: number[] = [5, 10, 25, 100];

    /**
     * If the table should use external paging
     * otherwise its assumed that all data is preloaded.
     */
    @Input() externalPaging = true;

    /**
     * If the table should use external sorting or
     * the built-in basic sorting.
     */
    @Input() externalSorting = true;

    @Input() showCheckbox = false;

    @Input() paginationServerSide = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Input() rowIdentity: (value?: T) => any = () => null;

    @Input() selected: T[] = [];

    @Output() selectedItems = new EventEmitter<T>();
    @Output() rowClick = new EventEmitter<T>();

    rows!: T[];

    fetchError: string | null = null;

    get isLoading$(): Observable<boolean> {
        return this.datasource.isLoading;
    }

    get page() {
        return this.datasource?.tablePage;
    }

    get isServerPaginated(): boolean {
        return this.externalPaging && this.datasource instanceof AbstractDataSource;
    }

    get firstNumberPage(): number {
        return this.isServerPaginated ? 1 : 0;
    }

    get appBasePath(): string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const basePath = (this._appConfig as any)?.frontend?.appBasePath || '';
        return basePath;
    }

    constructor() {}

    ngOnInit(): void {
        this._datasourceStart();
        this._subscribeToDatasource();
        this._subscribeToDatasourceErrors();
        this._initConfig();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.['datasource']?.currentValue) {
            this._subscribeToDatasource();
            this._subscribeToDatasourceErrors();
        }
    }

    // TODO: define sort event object
    changeSort(sort: { multisortmeta: SortMeta[] }): void {
        const tableSort: TableSort[] = sort.multisortmeta.map((meta) => ({
            prop: meta.field,
            dir: meta.order === 1 ? 'asc' : 'desc'
        }));
        this.datasource.sorting(tableSort);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSelected(event: any): void {
        this.selectedItems.emit(event);
    }

    onLazyLoad(event: TableLazyLoadEvent): void {
        this.datasource.paging((event.first ?? 0) / (event.rows ?? 1), event.rows ?? 0);
    }

    onPageChangePaginator(event: PaginatorState): void {
        this.datasource.paging(event.page, event.rows);
    }

    onActivate(event: { type: string; row: T | undefined }): void {
        if (event.type === 'click') {
            this.rowClick.emit(event.row);
        }
    }

    private _datasourceStart(): void {
        this.datasource.start();
    }

    private _subscribeToDatasource(): void {
        this.datasource.rows$.subscribe((rows) => {
            this.rows = rows;
            this.fetchError = null;
        });
    }

    private _subscribeToDatasourceErrors(): void {
        this.datasource.rows$.subscribe({
            error: (err) => {
                this.rows = this.rows ?? [];
                this.fetchError = err.message;
                // TODO: Add error toast
            }
        });
    }

    private _initConfig() {
        this.config = {
            ...getDefaultTableConfig(),
            ...this.config
        };
    }
}
